import type { VetorTabletop } from './tipos'

/**
 * A flexão de uma escada apoiada, no referencial dela (o pé na origem, o eixo
 * subindo por `[0, sen θ, cos θ]`). A escada se curva no plano vertical que a
 * contém, para o lado do peso: `n = [0, -cos θ, sen θ]` (a parte da gravidade
 * perpendicular ao eixo). O pé não se move; a flecha cresce como a de uma viga
 * engastada no pé. TypeScript puro e sem dependências: o desenho e os pontos de
 * ancoragem usam a mesma curva.
 */

/** Forma normalizada da flecha: 0 no pé, 1 no topo (viga engastada com carga na ponta). */
export const formaDaFlexao = (fracao: number): number => {
  const x = Math.min(1, Math.max(0, fracao))
  return (x * x * (3 - x)) / 2
}

/** O sentido da flexão no referencial da escada: perpendicular ao eixo, para baixo. */
export const sentidoDaFlexao = (anguloGraus: number): VetorTabletop => {
  const a = (anguloGraus * Math.PI) / 180
  return [0, -Math.cos(a), Math.sin(a)]
}

/** O deslocamento (m) a `s` metros do pé, numa escada de `comprimentoM` com flecha `flexaoM` no topo. */
export const flechaEm = (flexaoM: number, comprimentoM: number, s: number): number =>
  flexaoM * formaDaFlexao(comprimentoM > 0 ? s / comprimentoM : 0)

/** Leva um ponto do referencial da escada para o referencial dela já flexionada. */
export function pontoFlexionado(local: VetorTabletop, anguloGraus: number, comprimentoM: number, flexaoM: number): VetorTabletop {
  if (!(Math.abs(flexaoM) > 0)) return local
  const a = (anguloGraus * Math.PI) / 180
  const s = local[1] * Math.sin(a) + local[2] * Math.cos(a)
  const d = flechaEm(flexaoM, comprimentoM, s)
  const n = sentidoDaFlexao(anguloGraus)
  return [local[0] + n[0] * d, local[1] + n[1] * d, local[2] + n[2] * d]
}
